/**
 * Utility functions for meeting-related calculations and formatting
 */

import { normalizeWeekdayValue, parseWeekdays } from './meetingLocale';

type TranslationFunction = (key: string, params?: Record<string, any>) => string;

/**
 * Formats weekdays intelligently from database format to user-friendly format
 * @param weekdays - String format: "Segunda-feira; Terça-feira; Quarta-feira; Quinta-feira; Sexta-feira; Sábado; Domingo"
 * @param t - Translation function
 * @returns Formatted string like "De segunda-feira à sexta-feira", "Todos os dias", etc.
 */
export function formatWeekdays(weekdays: string, t: TranslationFunction): string {
  if (!weekdays || weekdays.trim() === '') {
    return t('meeting.notSpecified');
  }

  // Split and clean weekdays
  const days = weekdays.split(';').map(day => day.trim());
  
  // All 7 days
  if (days.length === 7) {
    return t('meeting.everyDay');
  }
  
  // Weekdays (Monday to Friday)
  const weekdaysOnly = ['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira'];
  const isWeekdaysOnly = weekdaysOnly.every(day => days.includes(day)) && days.length === 5;
  
  if (isWeekdaysOnly) {
    return t('meeting.mondayToFriday');
  }
  
  // Weekend only
  const weekendOnly = ['Sábado', 'Domingo'];
  const isWeekendOnly = weekendOnly.every(day => days.includes(day)) && days.length === 2;
  
  if (isWeekendOnly) {
    return t('meeting.weekends');
  }
  
  // Single day repeated
  if (days.length === 1) {
    const day = days[0].toLowerCase();
    return t('meeting.everyDayName', { day });
  }
  
  // Two consecutive days
  if (days.length === 2) {
    return `${days[0]} ${t('meeting.and')} ${days[1]}`;
  }
  
  // Three or more days - just list them
  if (days.length <= 3) {
    const lastDay = days.pop();
    return `${days.join(', ')} ${t('meeting.and')} ${lastDay}`;
  }
  
  // More than 3 days - show first few and "e outros"
  const firstTwo = days.slice(0, 2);
  return `${firstTwo.join(', ')} ${t('meeting.andOthers')}`;
}

/**
 * Calculates the time difference and returns a formatted message
 * @param meetingTime - Meeting time in "HH:mm" format (e.g., "14:00")
 * @param t - Translation function
 * @returns Object with message and status
 */
export function getMeetingTimeStatus(
  meetingTime: string,
  meetingWeekday: string,
  t: TranslationFunction
): {
  message: string;
  status: 'upcoming' | 'ongoing' | 'past' | 'error';
  minutesDifference: number;
} {
  try {
    if (!meetingTime || !meetingTime.includes(':')) {
      return {
        message: t('meeting.timeNotSpecified'),
        status: 'error',
        minutesDifference: 0
      };
    }

    const now = new Date();
    const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();
    const todayNumber = now.getDay();

    // Parse meeting time
    const [hourStr, minuteStr] = meetingTime.split(':');
    const meetingHour = parseInt(hourStr, 10);
    const meetingMinute = parseInt(minuteStr, 10);
    const meetingTimeInMinutes = meetingHour * 60 + meetingMinute;

    // Parse meeting weekdays so the delta accounts for the actual next
    // occurrence — previously this compared HH:mm only, so a Monday 12:00
    // meeting showed "starts in 1 hour" on Sunday at 11:00 (off by 24h).
    const weekdayMap: Record<string, number> = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6,
    };
    const meetingDayNumbers = meetingWeekday
      ? parseWeekdays(meetingWeekday)
          .map(d => normalizeWeekdayValue(d))
          .map(d => weekdayMap[d])
          .filter((n): n is number => n !== undefined)
      : [];

    let minutesDifference: number;
    if (meetingDayNumbers.length === 0) {
      // Legacy fallback: no weekday info → same-day delta (pre-fix behavior).
      minutesDifference = meetingTimeInMinutes - currentTimeInMinutes;
    } else if (
      meetingDayNumbers.includes(todayNumber) &&
      meetingTimeInMinutes - currentTimeInMinutes >= -120
    ) {
      // Today is a meeting day and we're still within the 2-hour "ongoing"
      // window (matches getMeetingDisplayTime's live tolerance).
      minutesDifference = meetingTimeInMinutes - currentTimeInMinutes;
    } else {
      // Find the next meeting day in the upcoming week and add the day delta.
      let daysUntilNext = 7;
      for (let i = 1; i <= 7; i++) {
        if (meetingDayNumbers.includes((todayNumber + i) % 7)) {
          daysUntilNext = i;
          break;
        }
      }
      minutesDifference = daysUntilNext * 1440 + (meetingTimeInMinutes - currentTimeInMinutes);
    }

    // Determine status and message
    if (minutesDifference > 60) {
      // More than 1 hour ahead
      const hours = Math.floor(minutesDifference / 60);
      const minutes = minutesDifference % 60;
      
      if (minutes === 0) {
        return {
          message: t('meeting.startsInHours', { 
            hours, 
            unit: hours === 1 ? t('meeting.hour') : t('meeting.hours') 
          }),
          status: 'upcoming',
          minutesDifference
        };
      } else {
        return {
          message: t('meeting.startsInHoursMinutes', { 
            hours, 
            minutes: minutes.toString().padStart(2, '0') 
          }),
          status: 'upcoming',
          minutesDifference
        };
      }
    } else if (minutesDifference > 0) {
      // Less than 1 hour ahead
      return {
        message: t('meeting.startsInMinutes', { 
          minutes: minutesDifference, 
          unit: minutesDifference === 1 ? t('meeting.minute') : t('meeting.minutes') 
        }),
        status: 'upcoming',
        minutesDifference
      };
    } else if (minutesDifference >= -120) {
      // Started but within 2 hours (considered live/ongoing)
      return {
        message: t('meeting.liveNow'),
        status: 'ongoing',
        minutesDifference
      };
    } else {
      // More than 2 hours past
      const elapsedMinutes = Math.abs(minutesDifference);
      const hours = Math.floor(elapsedMinutes / 60);
      const minutes = elapsedMinutes % 60;
      
      if (hours > 0) {
        return {
          message: t('meeting.endedHoursAgo', { 
            hours, 
            minutes: minutes.toString().padStart(2, '0') 
          }),
          status: 'past',
          minutesDifference
        };
      } else {
        return {
          message: t('meeting.endedMinutesAgo', { 
            minutes: elapsedMinutes, 
            unit: elapsedMinutes === 1 ? t('meeting.minute') : t('meeting.minutes') 
        }),
          status: 'past',
          minutesDifference
        };
      }
    }
  } catch (error) {
    console.error('Error calculating meeting time status:', error);
    return {
      message: t('meeting.timeError'),
      status: 'error',
      minutesDifference: 0
    };
  }
}

/**
 * Determines if a meeting is live/ongoing based on time
 * @param meetingTime - Meeting time in "HH:mm" format
 * @param t - Translation function
 * @returns true if meeting is currently happening
 */
export function isMeetingLive(meetingTime: string, meetingWeekday: string, t: TranslationFunction): boolean {
  const timeStatus = getMeetingTimeStatus(meetingTime, meetingWeekday, t);
  return timeStatus.status === 'ongoing';
}

/**
 * Formats time for display - removes seconds if present (HH:mm:ss -> HH:mm)
 * @param time - Time in "HH:mm" or "HH:mm:ss" format
 * @returns Formatted time string in HH:mm format
 */
export function formatDisplayTime(time: string): string {
  try {
    if (!time || !time.includes(':')) {
      return time;
    }

    const parts = time.split(':');
    const hour = parseInt(parts[0], 10);
    const minute = parseInt(parts[1], 10);
    
    // Return in 24h format without seconds (common in Brazil)
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error('Error formatting display time:', error);
    return time;
  }
}

/**
 * UNIFIED function to get meeting display time considering both time AND weekdays
 * This ensures consistent display across all pages (encontros-online, group, meeting detail)
 * 
 * @param meetingTime - Meeting time in "HH:mm" format (e.g., "14:00")
 * @param meetingWeekday - Comma-separated weekdays (e.g., "monday,friday")
 * @param t - Translation function
 * @returns Object with displayText and isLive status
 */
export function getMeetingDisplayTime(
  meetingTime: string, 
  meetingWeekday: string, 
  t: TranslationFunction
): { displayText: string; isLive: boolean } {
  try {
    if (!meetingTime || !meetingTime.includes(':')) {
      return { displayText: t('meeting.timeNotSpecified'), isLive: false };
    }

    const now = new Date();
    const [hours, minutes] = meetingTime.split(':').map(Number);
    
    // Weekday mapping
    const weekdayMap: Record<string, number> = {
      'sunday': 0,
      'monday': 1,
      'tuesday': 2,
      'wednesday': 3,
      'thursday': 4,
      'friday': 5,
      'saturday': 6,
    };
    
    // Parse and normalize meeting weekdays to English keys
    const meetingDays = parseWeekdays(meetingWeekday).map(day => normalizeWeekdayValue(day));
    const meetingDayNumbers = meetingDays.map(day => weekdayMap[day]).filter(num => num !== undefined);
    
    // Current time info
    const todayNumber = now.getDay();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const meetingMinutes = hours * 60 + minutes;
    
    // Check if meeting is live: from 10 minutes BEFORE start through 2 hours
    // AFTER start, on a valid weekday. The 10-min lead-in matches the
    // pre-meeting push notification, so the Entrar button is available as
    // soon as the user is alerted (no "we told you to join but the button
    // isn't there yet" gap).
    const minutesDifference = meetingMinutes - currentMinutes;
    if (meetingDayNumbers.includes(todayNumber) && minutesDifference >= -120 && minutesDifference <= 10) {
      return { displayText: t('meeting.liveNow'), isLive: true };
    }
    
    // Check if meeting is today and hasn't passed yet
    if (meetingDayNumbers.includes(todayNumber) && currentMinutes < meetingMinutes) {
      return { displayText: `${t('common.today')} ${t('meetings.detail.at')} ${formatDisplayTime(meetingTime)}`, isLive: false };
    }
    
    // Find next occurrence
    let daysUntilNext = 7; // Default to a week away
    for (let i = 1; i <= 7; i++) {
      const checkDay = (todayNumber + i) % 7;
      if (meetingDayNumbers.includes(checkDay)) {
        daysUntilNext = i;
        break;
      }
    }
    
    // If tomorrow
    if (daysUntilNext === 1) {
      return { displayText: `${t('common.tomorrow')} ${t('meetings.detail.at')} ${formatDisplayTime(meetingTime)}`, isLive: false };
    }
    
    // For dates further out, show specific date
    const nextDate = new Date(now);
    nextDate.setDate(now.getDate() + daysUntilNext);
    
    const monthNames = [
      t('common.months.january'), t('common.months.february'), t('common.months.march'), 
      t('common.months.april'), t('common.months.may'), t('common.months.june'),
      t('common.months.july'), t('common.months.august'), t('common.months.september'), 
      t('common.months.october'), t('common.months.november'), t('common.months.december')
    ];
    
    const day = nextDate.getDate();
    const month = monthNames[nextDate.getMonth()];
    
    return { displayText: `${day} ${t('common.of')} ${month} ${t('meetings.detail.at')} ${formatDisplayTime(meetingTime)}`, isLive: false };
  } catch (error) {
    console.error('Error calculating meeting display time:', error);
    return { displayText: t('meeting.timeError'), isLive: false };
  }
}